import React, { useState } from "react";
import CodeDropdown from "../webapp/CodeDropdown";
import { CoffeeJourney } from "../webapp/CoffeeJourney";
import { Document, Page, pdfjs } from "react-pdf";
import {
  getPdfFilename,
  getPdfPageForSection,
  TEST_COMPLIANCE_DATA,
} from "@/services/pdfService";

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

interface CodeOption {
  id: string;
  label: string;
  description?: string;
}

interface CodeAnalysisStepProps {
  onComplete: () => void;
  onNext: () => void;
  onPrevious: () => void;
  canProceed: boolean;
}

const CodeAnalysisStep: React.FC<CodeAnalysisStepProps> = ({
  onComplete,
  onNext,
  onPrevious,
  canProceed,
}) => {
  const [selectedOption, setSelectedOption] = useState<CodeOption>({
    id: "FBC-115-163",
    label: "FBC - Accessibility (Eighth Edition, 2023)",
    description: "Florida Building Code - Accessiblity",
  });
  const [isExtracting, setIsExtracting] = useState(false);
  const [showCoffeeJourney, setShowCoffeeJourney] = useState(false);
  const [showStartAnalyzing, setShowStartAnalyzing] = useState(false);
  const [jsonResult, setJsonResult] = useState<string>("");
  const [showCodeDisplay, setShowCodeDisplay] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(1); // Start on page 1, jump to 119 after analysis
  const [numPages, setNumPages] = useState<number>(0);

  // Store JSON results per code option
  const [codeResults, setCodeResults] = useState<
    Record<
      string,
      {
        jsonResult: string;
        showCodeDisplay: boolean;
        showStartAnalyzing: boolean;
        pdfPage: number;
      }
    >
  >({});

  // Helper function to get the correct PDF filename for an option
  const getOptionPdfFilename = (option: CodeOption): string => {
    return getPdfFilename(option.label);
  };

  // Helper function to get the initial page for each code option
  const getInitialPage = (option: CodeOption): number => {
    // Always start on page 1 initially
    return 1;
  };

  const handleSelectCode = (option: CodeOption) => {
    setSelectedOption(option);

    // Set the initial page for this code option
    setCurrentPage(getInitialPage(option));

    // Load saved state for this code option, or reset to initial state
    const savedState = codeResults[option.id];
    if (savedState) {
      setJsonResult(savedState.jsonResult);
      setShowCodeDisplay(savedState.showCodeDisplay);
      setShowStartAnalyzing(savedState.showStartAnalyzing);
      setCurrentPage(savedState.pdfPage);
    } else {
      setJsonResult("");
      setShowCodeDisplay(false);
      setShowStartAnalyzing(false);
      setCurrentPage(1); // Reset to page 1 for new codes
    }

    // Always reset these states when switching
    setShowCoffeeJourney(false);
    setIsExtracting(false);
  };

  // This function is for internal use by react-pdf.
  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  const onDocumentLoadError = (error: Error) => {
    // Handle PDF loading error
  };

  const handleExtract = async () => {
    setIsExtracting(true);
    setShowCoffeeJourney(true);
    setShowStartAnalyzing(false);
    setJsonResult("");
    setShowCodeDisplay(false);

    try {
      // Load real FBC regulations data for FBC-115-163
      if (selectedOption.id === "FBC-115-163") {
        const response = await fetch("/resources/fbc_regulations.json");
        const fbcRegulations = await response.json();

        // Show coffee journey for 6 seconds before showing results
        setTimeout(() => {
          setShowCoffeeJourney(false);
          setIsExtracting(false);
          setShowStartAnalyzing(true);

          const jsonString = JSON.stringify(fbcRegulations, null, 2);
          setJsonResult(jsonString);
          setShowCodeDisplay(true);

          // Jump to page 119 for FBC after analysis is complete
          setCurrentPage(119);

          // Scroll to page 119 after a short delay to ensure it's rendered
          setTimeout(() => {
            const pageElement = document.querySelector(
              `[data-page-number="119"]`
            );

            if (pageElement) {
              const pdfContainer = pageElement.closest(".overflow-auto");

              if (pdfContainer) {
                // Get the position of page 119 relative to the container
                const pageRect = pageElement.getBoundingClientRect();
                const containerRect = pdfContainer.getBoundingClientRect();

                // Calculate how much we need to scroll to get to page 119
                const scrollToPage119 = pageElement.offsetTop;

                // Scroll to page 119 first
                pdfContainer.scrollTop = scrollToPage119;

                // Then scroll to the bottom of page 119
                setTimeout(() => {
                  const pageHeight = pageElement.scrollHeight;
                  const containerHeight = pdfContainer.clientHeight;
                  const scrollToBottom =
                    scrollToPage119 +
                    pageHeight -
                    containerHeight -
                    pageHeight * 0.75;

                  pdfContainer.scrollTo({
                    top: scrollToBottom,
                    behavior: "smooth",
                  });
                }, 300);
              }
            }
          }, 500);

          // Store the result in codeResults for persistence
          setCodeResults((prev) => ({
            ...prev,
            [selectedOption.id]: {
              jsonResult: jsonString,
              showCodeDisplay: true,
              showStartAnalyzing: true,
              pdfPage: 119,
            },
          }));

          onComplete(); // Mark step as completed
        }, 6000); // 6 seconds for the full coffee journey
        return;
      }

      // Load real NFPA regulations data for NFPA
      if (selectedOption.id === "NFPA") {
        const response = await fetch("/resources/nfpa_regulations.json");
        const nfpaRegulations = await response.json();

        // Wait for coffee animation to complete (6 seconds)
        setTimeout(() => {
          setShowCoffeeJourney(false);
          setIsExtracting(false);
          setShowStartAnalyzing(true);

          const jsonString = JSON.stringify(nfpaRegulations, null, 2);
          setJsonResult(jsonString);
          setShowCodeDisplay(true);

          // Store the result in codeResults for persistence
          setCodeResults((prev) => ({
            ...prev,
            [selectedOption.id]: {
              jsonResult: jsonString,
              showCodeDisplay: true,
              showStartAnalyzing: true,
              pdfPage: 1,
            },
          }));

          onComplete(); // Mark step as completed
        }, 6000); // 6 seconds for the full coffee journey
        return;
      }

      // For other codes, use test data
      setTimeout(() => {
        setShowCoffeeJourney(false);
        setIsExtracting(false);
        setShowStartAnalyzing(true);

        // Use test JSON data for non-FBC codes
        const testJsonData = [
          {
            measurements: {
              centerline: { minimum: 16, maximum: 18 },
            },
            page: 4,
            regulation_number: "604.2",
            requirement:
              "The water closet shall be positioned with a wall or partition to the rear and to one side. The centerline of the water closet shall be 16 inches minimum to 18 inches maximum from the side wall or partition.",
            title: "Location",
          },
          {
            measurements: { open_time: { minimum: 10 } },
            page: 15,
            regulation_number: "606.4",
            requirement:
              "Controls for faucets shall comply with 309. Hand-operated metering faucets shall remain open for 10 seconds minimum.",
            title: "Faucets",
          },
          {
            measurements: {},
            page: 15,
            regulation_number: "606.5",
            requirement:
              "Water supply and drain pipes under lavatories and sinks shall be insulated or otherwise configured to protect against contact. There shall be no sharp or abrasive surfaces under lavatories and sinks.",
            title: "Exposed Pipes and Surfaces",
          },
          {
            measurements: { clearance_width: { minimum: 30 } },
            page: 15,
            regulation_number: "607.2",
            requirement:
              "Clearance in front of bathtubs shall extend the length of the bathtub and shall be 30 inches (760 mm) wide minimum.",
            title: "Clearance",
          },
          {
            measurements: {
              knee_clearance: { minimum: 24 },
              rim_or_counter_surface: { maximum: 31 },
            },
            page: 15,
            regulation_number: "4",
            requirement:
              "A knee clearance of 24 inches minimum above the finish floor or ground shall be permitted at lavatories and sinks used primarily by children 6 through 12 years where the rim or counter surface is 31 inches maximum above the finish floor or ground.",
            title: "Knee Clearance for Children",
          },
        ];

        const jsonString = JSON.stringify(testJsonData, null, 2);
        setJsonResult(jsonString);
        setShowCodeDisplay(true);

        // Jump to page 119 for FBC after analysis is complete
        if (selectedOption.id === "FBC-115-163") {
          setCurrentPage(119);

          // Scroll to page 119 after a short delay to ensure it's rendered
          setTimeout(() => {
            const pageElement = document.querySelector(
              `[data-page-number="119"]`
            );

            if (pageElement) {
              const pdfContainer = pageElement.closest(".overflow-auto");

              if (pdfContainer) {
                // Get the position of page 119 relative to the container
                const pageRect = pageElement.getBoundingClientRect();
                const containerRect = pdfContainer.getBoundingClientRect();

                // Calculate how much we need to scroll to get to page 119
                const scrollToPage119 = pageElement.offsetTop;

                // Scroll to page 119 first
                pdfContainer.scrollTop = scrollToPage119;

                // Then scroll to the bottom of page 119
                setTimeout(() => {
                  const pageHeight = pageElement.scrollHeight;
                  const containerHeight = pdfContainer.clientHeight;
                  const scrollToBottom =
                    scrollToPage119 +
                    pageHeight -
                    containerHeight -
                    pageHeight * 0.75;

                  pdfContainer.scrollTo({
                    top: scrollToBottom,
                    behavior: "smooth",
                  });
                }, 300);
              }
            } else {
              // Try alternative selector
              const altPageElement = document.querySelector(
                '.react-pdf__Page[data-page-number="119"]'
              );
            }
          }, 1500);
        }

        // Save the results for this code option
        setCodeResults((prev) => ({
          ...prev,
          [selectedOption.id]: {
            jsonResult: jsonString,
            showCodeDisplay: true,
            showStartAnalyzing: true,
            pdfPage: selectedOption.id === "FBC-115-163" ? 119 : 1,
          },
        }));

        onComplete(); // Mark step as completed
      }, 6000); // 6 seconds for the full coffee journey
    } catch (error) {
      setShowCoffeeJourney(false);
      setIsExtracting(false);
      setShowStartAnalyzing(true);
    }
  };

  const handleStartAnalyzing = () => {
    onNext();
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-gray-800 mb-4">
          Step 2: Code Analysis
        </h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Let's extract regulations from your building codes and convert them
          into machine-readable format
        </p>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
        {/* Header Section */}
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-4">
            Regulation to Machine Readable File
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Select a code and get a glimpse of Hero's superpowers
          </p>
        </div>

        {/* Coffee Journey Animation - Show above existing content */}
        {showCoffeeJourney && (
          <div className="mb-8">
            <CoffeeJourney />
          </div>
        )}

        {/* Dropdown & Button Section */}
        <div className="flex flex-col items-center space-y-6 mb-8">
          <div className="w-full max-w-2xl">
            <label
              htmlFor="code-select"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Available Codes
            </label>
            <div className="flex items-end space-x-4">
              <div className="flex-1">
                <CodeDropdown
                  selectedOption={selectedOption}
                  onSelect={handleSelectCode}
                />
              </div>
              <div>
                <button
                  onClick={
                    selectedOption.id === "FBC-115-163" ||
                    selectedOption.id === "NFPA"
                      ? handleExtract
                      : undefined
                  }
                  disabled={
                    (selectedOption.id !== "FBC-115-163" &&
                      selectedOption.id !== "NFPA") ||
                    isExtracting
                  }
                  className={`w-full font-medium py-2 px-4 rounded-lg transition-all duration-200 shadow-sm ${
                    (selectedOption.id === "FBC-115-163" ||
                      selectedOption.id === "NFPA") &&
                    !isExtracting
                      ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white cursor-pointer hover:shadow-lg hover:-translate-y-0.5"
                      : "bg-gray-600 text-gray-400 cursor-not-allowed opacity-50"
                  }`}
                  title={
                    selectedOption.id === "FBC-115-163" ||
                    selectedOption.id === "NFPA"
                      ? "Click to extract PDF content"
                      : "Coming Soon - This code is not yet available for extraction"
                  }
                >
                  {isExtracting
                    ? "⏳ Processing..."
                    : selectedOption.id === "FBC-115-163" ||
                        selectedOption.id === "NFPA"
                      ? "🚀 Extract"
                      : "⏳ Coming Soon"}
                </button>
              </div>
            </div>
          </div>

          {/* Start Analyzing Button - Show after extraction */}
          {showStartAnalyzing && (
            <div className="w-full max-w-2xl space-y-4">
              <button
                onClick={handleStartAnalyzing}
                className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
              >
                🚀 Continue to BIM Viewer
              </button>
            </div>
          )}
        </div>

        {/* PDF Viewer and JSON Section */}
        <div className="bg-gray-50 rounded-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-2xl font-bold text-gray-800">
              PDF Viewer & Extraction
            </h3>
          </div>

          <div className="grid grid-cols-12 gap-4 items-start">
            {/* PDF Preview */}
            <div className="col-span-5 bg-white rounded-lg p-4 border border-gray-200 shadow-sm min-h-[400px]">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <span className="text-blue-500">📄</span>
                  Source PDF
                </h4>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() =>
                      window.open(
                        `/resources/${getOptionPdfFilename(selectedOption)}`,
                        "_blank"
                      )
                    }
                    className="px-3 py-1 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors flex items-center gap-1"
                  >
                    <span>👁️</span>
                    Preview
                  </button>
                </div>
              </div>
              <div
                className="border border-gray-300 rounded-md p-2 bg-white overflow-auto"
                style={{ height: "600px" }}
              >
                <Document
                  file={`/resources/${getOptionPdfFilename(selectedOption)}`}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={onDocumentLoadError}
                  loading={
                    <div className="flex items-center justify-center p-4 bg-white">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-gray-500 text-sm">
                        Loading...
                      </span>
                    </div>
                  }
                  error={
                    <div className="text-center p-4 text-red-500 bg-white">
                      <p className="text-sm">Failed to load PDF</p>
                      <p className="text-xs text-gray-500 mt-1">
                        File: /resources/{selectedOption.id}.pdf
                      </p>
                    </div>
                  }
                >
                  {Array.from(new Array(numPages), (el, index) => (
                    <Page
                      key={`page_${index + 1}`}
                      pageNumber={index + 1}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      scale={1.2}
                      className="shadow-lg mb-4"
                    />
                  ))}
                </Document>
              </div>
            </div>

            {/* Arrow */}
            <div className="col-span-2 flex items-center justify-center">
              <div className="flex flex-col items-center gap-2">
                <div
                  className={`text-3xl transition-all duration-500 ${
                    isExtracting
                      ? "text-blue-500 animate-pulse"
                      : "text-green-500"
                  }`}
                >
                  {isExtracting ? "⏳" : "➡️"}
                </div>
                {isExtracting && (
                  <div className="text-xs text-gray-500 text-center">
                    Processing...
                  </div>
                )}
              </div>
            </div>

            {/* JSON Results */}
            <div className="col-span-5 bg-white rounded-lg p-4 border border-gray-200 shadow-sm min-h-[400px]">
              <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <span className="text-green-500">📋</span>
                Extracted Data
              </h4>
              {isExtracting ? (
                <div
                  className="flex flex-col items-center justify-center bg-gray-50 rounded-lg border border-gray-200"
                  style={{ height: "600px" }}
                >
                  <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mb-6"></div>
                  <p className="text-gray-700 text-center text-lg font-medium mb-2">
                    Analyzing PDF content...
                  </p>
                  <p className="text-gray-500 text-center text-sm">
                    Extracting regulations and measurements
                  </p>
                  <div className="mt-4 flex items-center gap-2 text-blue-500">
                    <div className="animate-pulse">🔍</div>
                    <span className="text-sm">AI Processing...</span>
                  </div>
                </div>
              ) : showCodeDisplay && jsonResult ? (
                <div
                  className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden"
                  style={{ height: "600px" }}
                >
                  <pre className="text-sm text-gray-700 bg-gray-50 p-4 rounded-lg overflow-auto h-full">
                    {jsonResult.split("\n").map((line, index) => {
                      // Highlight lines containing "regulation_number": "604.2" or "title": "Location"
                      if (
                        line.includes('"regulation_number": "604.2"') ||
                        line.includes('"title": "Location"')
                      ) {
                        return (
                          <div
                            key={index}
                            className="bg-yellow-200 border-l-4 border-yellow-500 pl-2 -ml-2"
                          >
                            {line}
                          </div>
                        );
                      }
                      return <div key={index}>{line}</div>;
                    })}
                  </pre>
                </div>
              ) : (
                <div
                  className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden"
                  style={{ height: "600px" }}
                >
                  <div className="flex items-center justify-center h-full text-gray-500">
                    <div className="text-center">
                      <div className="text-4xl mb-2">📄</div>
                      <p className="text-lg">Ready to extract</p>
                      <p className="text-sm text-gray-400 mt-1">
                        Click Extract to begin
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center mt-8">
          <button
            onClick={onPrevious}
            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
          >
            ← Previous
          </button>

          <div className="text-sm text-gray-500">
            Step 2 of 3: Code Analysis
          </div>
        </div>
      </div>
    </div>
  );
};

export { CodeAnalysisStep };
